import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, MaskLines, SectionLabel } from "./Motion";
import { PILLARS, PHOTOS, CABINET, PARCOURS } from "@/lib/content";

export const Cabinet = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="cabinet" ref={ref} data-testid="cabinet-section" className="relative bg-[#FAF8F5] py-28 sm:py-36 lg:py-44">
      <div className="mx-auto grid max-w-[1400px] gap-16 px-5 sm:px-8 lg:grid-cols-12 lg:gap-12 lg:px-12">
        <div className="lg:col-span-5">
          <Reveal><SectionLabel index="01">Le cabinet</SectionLabel></Reveal>
          <h2 className="font-serif-d mt-8 text-4xl font-light leading-[1.08] text-[#1C2826] sm:text-5xl lg:text-6xl">
            <MaskLines lines={["Une avocate", <>à vos <em className="italic text-[#3D5A4C]">côtés</em>,</>, "du premier jour", "à la réparation."]} />
          </h2>
          <Reveal delay={0.1} className="relative mx-auto mt-14 w-full max-w-[420px] lg:mx-0">
            <div className="relative aspect-[4/5] overflow-hidden rounded-t-full border border-[#B39158]/40 p-3">
              <div className="h-full w-full overflow-hidden rounded-t-full">
                <motion.img style={{ y, scale: 1.12 }} src={PHOTOS.portrait} alt="Portrait de Maître Pauline Rège Chanal, avocate au barreau d'Aix-en-Provence"
                  data-testid="cabinet-portrait"
                  className="h-full w-full object-cover object-[center_30%]" loading="lazy" />
              </div>
            </div>
            <div className="absolute -bottom-8 -right-2 bg-[#FAF8F5] px-5 py-4 sm:-right-8" data-testid="cabinet-serment-badge">
              <p className="font-serif-d text-5xl font-light leading-none text-[#3D5A4C]">{CABINET.serment}</p>
              <p className="eyebrow mt-2 text-[#5b6763]">Prestation de serment</p>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-6 lg:col-start-7 lg:pt-24">
          <Reveal>
            <p className="font-serif-d text-2xl font-light leading-snug text-[#1C2826] sm:text-3xl">
              Inscrite au barreau d'Aix-en-Provence depuis {CABINET.serment}, Maître Pauline Rège Chanal consacre l'essentiel de son activité
              à la défense des victimes et à l'indemnisation de leurs préjudices.
            </p>
          </Reveal>
          <Reveal delay={0.05}>
            <div data-testid="cabinet-parcours" className="mt-8 space-y-5 text-base leading-relaxed text-[#3E4A47] sm:text-[1.05rem]">
              {PARCOURS.map((p, i) => <p key={i}>{p}</p>)}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-5 text-base leading-relaxed text-[#3E4A47] sm:text-[1.05rem]">
              Un accident ou une agression bouleverse une vie, parfois durablement. Face aux assureurs, aux experts et aux procédures,
              la victime se retrouve souvent seule, confrontée à des interlocuteurs rompus à ces questions. Le rôle du cabinet est de
              rétablir cet équilibre : vous informer de vos droits, constituer un dossier solide et défendre vos intérêts avec constance,
              en toute indépendance vis-à-vis des compagnies d'assurance.
            </p>
          </Reveal>
          <div className="mt-16 border-t border-[#3D5A4C]/15">
            {PILLARS.map((p, i) => (
              <Reveal key={p.t} delay={i * 0.08}>
                <div data-testid={`cabinet-pillar-${i}`} className="group grid gap-3 border-b border-[#3D5A4C]/15 py-8 sm:grid-cols-12 sm:gap-6">
                  <span className="font-serif-d text-sm italic text-[#B39158] sm:col-span-1 sm:pt-2">0{i + 1}</span>
                  <h3 className="font-serif-d text-3xl text-[#1C2826] transition-transform duration-500 group-hover:translate-x-2 sm:col-span-4">{p.t}</h3>
                  <p className="text-[0.98rem] leading-relaxed text-[#3E4A47] sm:col-span-7">{p.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
